import React, { useState } from "react";
import useAxiosInstance from "../../../../../lib/useAxiosInstance";

const AddChartModal = ({ survey, chartType, onClose, onSave }) => {
  const axiosInstance = useAxiosInstance();

  const [heading, setHeading] = useState("");
  const [xAxisMetric, setXAxisMetric] = useState("count");
  const [yAxisQuestion, setYAxisQuestion] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isBarChart = chartType === "bar";
  const isProgressChart = chartType === "progress";
  const isPieChart = chartType === "pie";
  const isLegend = chartType === "legend";
  // 🔁 Common: Select Question handler (used for all chart types)
  const handleQuestionChange = (e) => {
    const value = e.target.value;
    setYAxisQuestion(value);

    const questionObj =
      survey?.questionIds?.find(
        (q) => q?.id === value || q?._id === value
      ) || null;

    setSelectedQuestion(questionObj);
  };

  // For ALL chart types we now require: Select Question + Heading
  const isConfirmDisabled =
    !heading.trim() || !yAxisQuestion || isSubmitting;
  const handleConfirm = async () => {
    if (isConfirmDisabled) return;

    const payload = {
      surveyId: survey?._id || survey?.id || null,
      questionId:
        selectedQuestion?._id || selectedQuestion?.id || null,
      chartType,
      heading: heading || null,
      // Send xAxisMetric for bar & pie, default to "count" otherwise
      xAxisMetric: isBarChart || isPieChart ? xAxisMetric : "count",
    };

    try {
      setIsSubmitting(true);

      const res = await axiosInstance.post(
        "/api/surveyAnalytics/chartConfig",
        payload
      );

      const savedConfig = res?.data?.data || res?.data;

      if (onSave) {
        onSave(savedConfig);
      }

      onClose();
    } catch (error) {
      console.error("Error saving chart config:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-100 p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
             {isBarChart
              ? "Configure Bar Chart"
              : isPieChart
              ? "Configure Pie Chart"
              : isProgressChart
              ? "Configure Progress Chart"
              : isLegend
              ? "Add Color Legend"
              : "Add data to chart"}
            </h2>
           <p className="mt-1 text-xs text-slate-500">
              {isLegend
                ? "Select a question and add a heading for the color legend."
                : isBarChart || isPieChart
                ? "Set heading, aggregation metric and choose the field from survey responses."
                : "Select a survey question whose responses will be used for this chart and provide a heading."}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 text-sm"
          >
            ✕
          </button>
        </div>

        {/* 🔹 COMMON BODY FOR ALL CHART TYPES */}
        <div className="space-y-4">
          {/* 1. Select Question (standard for all chart types) */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Select Question
            </label>
            <select
              className="w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm text-slate-800 shadow-inner focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300"
              value={yAxisQuestion}
              onChange={handleQuestionChange}
            >
              <option value="" disabled>
                Select question…
              </option>
              {survey?.questionIds?.map((q) => (
                <option key={q.id || q._id} value={q.id || q._id}>
                  {q.question_text}
                </option>
              ))}
            </select>
          </div>

          {/* 2. Heading (standard for all chart types) */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Heading
            </label>
            <input
              type="text"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              placeholder="e.g. Scheme Impact & Awareness"
              className="w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm text-slate-800 shadow-inner focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300"
            />
          </div>

          {/* 🔸 Extra field for BAR & PIE charts */}
          {(isBarChart || isPieChart) && !isLegend && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Aggregation Metric
              </label>
              <div className="inline-flex rounded-full bg-slate-100 p-0.5 text-xs">
                {["count", "average", "sum"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setXAxisMetric(option)}
                    className={`px-4 py-1.5 rounded-full font-semibold capitalize transition
                      ${
                        xAxisMetric === option
                          ? "bg-white text-sky-600 shadow-sm border border-sky-300"
                          : "text-slate-500"
                      }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[10px] text-slate-400">
                This decides whether the chart shows count, average or sum of
                responses.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-xl border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isConfirmDisabled}
            onClick={handleConfirm}
            className="rounded-xl bg-gradient-to-r from-sky-500 to-indigo-500 px-4 py-1.5 text-xs font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Saving..." : "Save configuration"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddChartModal;
